/**
 * Display formatting.
 *
 * **Money is a string on the wire and stays one here.** `node-pg` returns
 * `numeric` as a string and §9 forbids the `setTypeParser` override that would
 * turn it into a float, so the client must not undo that by parsing every
 * figure into a `number` on arrival. `formatMoney` reads a string and produces
 * a string; `sumMoney` adds with `BigInt` scaled to four decimal places —
 * `numeric(14,4)` — so a column total is exact rather than nearly exact.
 */

const MONEY_SCALE = 4n;
const SCALE_FACTOR = 10n ** MONEY_SCALE;

/** `"1234.5678"` -> `12345678n`. Tolerates a missing or short fraction. */
function toScaled(value: string): bigint {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = digits.split('.');
  const padded = (fraction + '0000').slice(0, 4);
  const magnitude = BigInt(whole || '0') * SCALE_FACTOR + BigInt(padded || '0');
  return negative ? -magnitude : magnitude;
}

function fromScaled(value: bigint): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / SCALE_FACTOR;
  const fraction = (magnitude % SCALE_FACTOR).toString().padStart(4, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

export type MoneyLike = string | number | null | undefined;

function normalize(value: MoneyLike): string | null {
  if (value === null || value === undefined) return null;
  const text = typeof value === 'number' ? value.toString() : value;
  return /^-?\d+(\.\d+)?$/.test(text.trim()) ? text.trim() : null;
}

/**
 * Exact addition over `numeric(14,4)` values. Used for the board column totals
 * and the account roll-ups the server does not pre-aggregate.
 */
export function sumMoney(values: readonly MoneyLike[]): string {
  let total = 0n;
  for (const value of values) {
    const text = normalize(value);
    if (text !== null) total += toScaled(text);
  }
  return fromScaled(total);
}

/**
 * `annual_units x asp`, exactly.
 *
 * The one multiplication the client does — the product-line table shows a
 * per-line value and its total — and the naive form is a float multiply whose
 * result is then rounded: `3 * 12.40` is `37.199999999999996` in binary
 * floating point, which prints as $37.20 but sums to a total that is off by
 * cents across a page. Units are whole, so this is one `BigInt` multiply
 * against the scaled ASP.
 */
export function multiplyMoney(units: MoneyLike, asp: MoneyLike): string {
  const unitsText = normalize(units);
  const aspText = normalize(asp);
  if (unitsText === null || aspText === null) return '0.0000';
  const wholeUnits = BigInt(unitsText.split('.')[0] ?? '0');
  return fromScaled(wholeUnits * toScaled(aspText));
}

const WHOLE_DOLLARS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const EXACT_DOLLARS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Whole dollars by default — a funnel is read at the million, and cents on a
 * pipeline total are noise. `exact` is for a single line's ASP, where cents
 * are the point.
 */
export function formatMoney(value: MoneyLike, options: { exact?: boolean } = {}): string {
  const text = normalize(value);
  if (text === null) return '—';
  const asNumber = Number(text);
  if (!Number.isFinite(asNumber)) return text;
  return options.exact ? EXACT_DOLLARS.format(asNumber) : WHOLE_DOLLARS.format(asNumber);
}

/** Compact, for a column header or a chip: `$1.2M`. */
export function formatMoneyCompact(value: MoneyLike): string {
  const text = normalize(value);
  if (text === null) return '—';
  const asNumber = Number(text);
  if (!Number.isFinite(asNumber)) return text;
  const abs = Math.abs(asNumber);
  if (abs >= 1_000_000) return `$${(asNumber / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(asNumber / 1_000)}k`;
  return WHOLE_DOLLARS.format(asNumber);
}

export function formatUnits(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber.toLocaleString('en-US') : String(value);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * A `date` column is a calendar date with no zone (§9). Parsing `2026-03-01`
 * with `new Date()` puts it at UTC midnight, which in California is the last
 * day of February — so the parts are read literally instead.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return DATE_FORMAT.format(new Date(Number(year), Number(month) - 1, Number(day)));
}

/** A `timestamptz`, rendered in the reader's zone — it is an instant, not a date. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${DATE_FORMAT.format(parsed)}, ${parsed.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * A `Date` as the value an `<input type="datetime-local">` holds: the
 * **local** wall clock, `YYYY-MM-DDTHH:mm`, no offset.
 *
 * Not `toISOString().slice(0, 16)`: that is the UTC wall clock, and the input
 * shows it as if it were local. The meeting form defaulted to exactly that,
 * so a meeting logged at 20:59 in California defaulted to 03:59 the next
 * day, and saving it unchanged stored an instant seven hours in the future.
 */
export function toDateTimeLocal(date: Date): string {
  return (
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  );
}

/**
 * The inverse: a `datetime-local` value, read as local time, as an ISO-8601
 * UTC instant for the API. Built from the components rather than handed to
 * `Date.parse`, so local-versus-UTC never depends on the parser.
 */
export function fromDateTimeLocal(value: string): string {
  const [datePart = '', timePart = '00:00'] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, hours ?? 0, minutes ?? 0).toISOString();
}

/**
 * The LOCAL calendar day an instant falls on, `YYYY-MM-DD`. An API timestamp
 * is UTC (`…Z`), so slicing its first ten characters gives the UTC day — an
 * evening meeting in California lands on tomorrow's calendar cell.
 */
export function localDateKey(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
}

export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function addDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  date.setDate(date.getDate() + days);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** Whole days from `isoDate` to today. Negative when the date is in the future. */
export function daysSince(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const then = Date.UTC(year, month - 1, day);
  const now = new Date();
  const midnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((midnight - then) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Enum labels
// ---------------------------------------------------------------------------

/** `design_engineer` -> `Design engineer`. Every enum in §5 is snake_case. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A business unit's label, from the `business_units` table (session 7) — pass
 * `useBusinessUnits()`'s map, or call its `label`. An unknown code is shown
 * humanized rather than blank, so a snapshot naming a code the table has never
 * heard of still reads.
 */
export function businessUnitLabel(
  value: string,
  units?: ReadonlyMap<string, { label: string }>,
): string {
  return units?.get(value)?.label ?? humanize(value);
}
